import { useEffect, useState, useCallback, useMemo } from "react";
import { MuiMaterials } from '../@index.js';
import * as global from '../data/global';
import { createApiEndpoint } from '../index.js';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import { outlinedActionButtonSx, withMenuScrollHintTheme } from './windowChrome/index.js';

const ALL_DEVICES = [{ BinId: 0, DeviceId: "All" }];

/** Site payload bins are optional; a missing cable/sensor list must not crash the CO₂ window. */
const readCarbonDioxideBins = () => {
    const bins = global.gw.selectedSite?.data?.bins;
    if (!Array.isArray(bins)) return [];
    return bins.map((b) => ({
        binName: b?.name ?? "",
        binId: b?.binId ?? 0,
        cableList: (Array.isArray(b?.cableList) ? b.cableList : []).map((c) => ({
            binId: b?.binId ?? 0,
            cableId: c?.id,
            cableName: c?.name ?? "",
            sensorList: (Array.isArray(c?.sensorList) ? c.sensorList : []).map((s) => ({
                binId: b?.binId ?? 0,
                cableId: s?.cableId,
                sensorId: s?.id,
                sensorName: s?.name ?? "",
            })),
        })),
    }));
};

const CarbonDioxideChartFilter = ({ chartFilterOptions, setChartFilterOptions }) => {
    const [binIds, setBinIds] = useState(readCarbonDioxideBins);
    const [deviceIds, setDeviceIds] = useState(null);
    const [dateFrom, setDateFrom] = useState(chartFilterOptions.dateFrom);
    const [dateTo, setDateTo] = useState(chartFilterOptions.dateTo);
    const [hourFrom, setHourFrom] = useState(0);
    const [hourTo, setHourTo] = useState(24);
    const [selectedBinId, setSelectedBinId] = useState([0]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("All");
    const [devices, setDevices] = useState(null);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(null);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    const theme = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        return MuiMaterials.createTheme({
    components: withMenuScrollHintTheme({
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: isLight ? '#000000' : '#f4cc8e',
                    fontSize: "18px",
                    borderColor: isLight ? '#666666' : 'rgb(244, 204, 142)',
                    ...(isLight && {
                        '&:not(.MuiInputLabel-shrink)': {
                            top: '50%',
                            transform: 'translateY(-50%)',
                            left: '14px',
                        },
                    }),
                },
                shrink: {
                    top: isLight ? '8px' : undefined,
                },
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: "18px",
                    background: isLight ? '#e0e0e0' : '#2d2d2d',
                    color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)',
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                sizeMedium: {
                    color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)'
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    color: isLight ? '#333333' : '#f4cc8e',
                    "& .MuiOutlinedInput-notchedOutline": {
                        border: isLight ? '0.5px solid #cccccc' : '0.5px solid #f4cc8e4d',
                    },
                    "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: isLight ? '0.5px solid #666666' : '0.5px solid #f4cc8e',
                        },
                    },
                    "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: isLight ? '0.5px solid #666666' : '0.5px solid #f4cc8e',
                        },
                    },
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    ".MuiInputLabel-root.Mui-focused": {
                        color: isLight ? '#000000' : '#f4cc8e',
                    },
                },
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    border: isLight ? '0.5px solid #cccccc' : '0.5px solid #f4cc8e4d',
                    ".MuiInputLabel-root.Mui-focused": {
                        color: isLight ? '#000000' : '#f4cc8e',
                    },
                    ".MuiSvgIcon-root": {
                        color: isLight ? '#333333' : '#f4cc8e',
                    },
                },
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: isLight ? '#333333' : '#f4cc8e',
                },
            }
        },
    }),
    });
    }, [forceUpdate]);

    useEffect(() => {
        const fetchSiteWeatherStationTablesTask = () => {
            return createApiEndpoint().fetchSiteWeatherStationTables("direct");
        };

        fetchSiteWeatherStationTablesTask().then(async (response) => {
            if (response && response.data && response.data.getSiteWeatherStationTables) {
                let arrayBuffer = global.base64StringToArrayBuffer(response.data.getSiteWeatherStationTables);
                let decompressed = await global.decompress(arrayBuffer, "gzip");
                let weatherStationTables = JSON.parse(decompressed);
                let weatherStationDevices = weatherStationTables?.WeatherStationDevice;
                if (!weatherStationDevices || weatherStationDevices === "") {
                    weatherStationDevices = [{
                        BinId: 0,
                        DeviceId: "All",
                    }];
                }
                else if (weatherStationDevices.length > 0 && weatherStationDevices[0].DeviceId !== "All") {
                    weatherStationDevices.splice(0, 0, {
                        BinId: 0,
                        DeviceId: "All",
                    })
                }

                let tempDeviceIds = weatherStationDevices.map(x => { return { DeviceId: x.DeviceId, BinId: x.BinId } });
                setDeviceIds(tempDeviceIds);

                let tempSelectedBinId = selectedBinId;
                tempSelectedBinId[0] = global.gw.selectedSite.carbonDioxideBinId ? global.gw.selectedSite.carbonDioxideBinId : selectedBinId[0];
                setSelectedBinId(tempSelectedBinId);

                global.gw.selectedSite.carbonDioxideBinId = null;
                global.gw.selectedSite.carbonDioxideBinName = null;

                renderDevices(tempSelectedBinId[0], tempDeviceIds);
            }
            else {
                // Missing weather-station tables is an empty CO₂ site, not a dead session.
                setDeviceIds(ALL_DEVICES);
                renderDevices(selectedBinId[0], ALL_DEVICES);
            }
        }).catch(function (error) {
            const status = error?.response?.status;
            const message = error?.message || "";
            const is401 = status === 401 || message.includes("401") || message.includes("Unauthorized");
            if (is401) return;
            console.error("CarbonDioxideChartFilter: weather station tables failed", error);
            setDeviceIds(ALL_DEVICES);
            renderDevices(selectedBinId[0], ALL_DEVICES);
        });
        
    }, []);

    const handleBinChange = (event) => {
        let tempSelectedBinId = selectedBinId;
        tempSelectedBinId[0] = event.target.value;
        setSelectedBinId([event.target.value]);

        renderDevices(tempSelectedBinId[0]);

        if (deviceIds && deviceIds.length > 0) {
            setSelectedDeviceId(deviceIds[0].DeviceId);
        }
    };

    const renderBins = () => {
        if (binIds && binIds.length > 0 && binIds[0].binName != "All") {
            binIds.splice(0, 0, {
                binId: 0,
                binName: "All",
            });
        }
        return (binIds.map((b, i) => (
            <MuiMaterials.MenuItem key={`bin-${b.binId}`} value={b.binId}>{b.binName}</MuiMaterials.MenuItem>
        )));
    };

    const renderDevices = (binId, deviceList) => {
        let binDevices = deviceList ? deviceList : deviceIds;
        if (binDevices) {
            if (binId != 0) {
                binDevices = binDevices.filter(d => d.BinId === binId);
            }
            if (binDevices && binDevices.length > 0 && binDevices[0].DeviceId != "All") {
                binDevices.splice(0, 0, {
                    BinId: binId,
                    DeviceId: "All",
                });
            }

            setDevices(binDevices.map((c, i) => (
                <MuiMaterials.MenuItem key={`device-${c.DeviceId}`} value={c.DeviceId}>{c.DeviceId}</MuiMaterials.MenuItem>
            )));
        }
    };

    const handleDeviceIdChange = (event) => {
        setSelectedDeviceId(event.target.value);
    };

    const renderFilterModal = () => {
        return (   
            <MuiMaterials.Modal
                key={`co2-filter-modal-${global.gw.theme}-${forceUpdate}`}
                open={isFilterModalOpen}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{
                    backgroundColor: getThemeColor("rgba(255, 255, 255, 0.45)", "rgba(0, 0, 0, 0.45)"),
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <MuiMaterials.Box
                    sx={{
                        background: getThemeColor('#ffffff', '#121212'),
                        margin: "auto 4px",
                        textAlign: "center",
                    }}
                >
                    <MuiMaterials.Button
                        sx={{
                            color: getThemeColor('#333333', '#f4cc8e'),
                            margin: "auto",
                            maxWidth: "40px",
                            maxHeight: "40px",
                            minWidth: "40px",
                            minHeight: "40px",
                            position: "absolute",
                            right: "2px",
                            top: "0",
                            '&:hover': {
                                backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                            },
                        }}
                        onClick={e => {
                            setIsFilterModalOpen(false);
                        }}
                    >
                        <CloseIcon />
                    </MuiMaterials.Button>
                    <MuiMaterials.Box
                        sx={{
                            //height: "100%",
                            //margin: "14px",
                            //overflowX: "auto",
                            marginTop: "10px",
                            marginBottom: "10px",
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            justifyContent: "center",
                        }}
                    >
                        <MuiMaterials.ThemeProvider theme={theme} key={`co2-filter-theme-${global.gw.theme}-${forceUpdate}`}>
                            <MuiMaterials.FormControl
                                sx={{
                                    width: "160px",
                                    margin: "10px 10px 10px 0px",
                                }}
                            >
                                <MuiMaterials.TextField
                                    select
                                    value={selectedBinId}
                                    label="Bin"
                                    onChange={handleBinChange}
                                >
                                    {renderBins()}
                                </MuiMaterials.TextField>
                            </MuiMaterials.FormControl>
                            <MuiMaterials.FormControl
                                sx={{
                                    width: "160px",
                                    margin: "10px 10px 10px 0px",
                                }}
                            >
                                <MuiMaterials.TextField
                                    select
                                    value={selectedDeviceId}
                                    label="Device Id"
                                    onChange={handleDeviceIdChange}
                                >
                                    {devices}
                                </MuiMaterials.TextField>
                            </MuiMaterials.FormControl>
                            <MuiMaterials.LocalizationProvider
                                dateAdapter={MuiMaterials.AdapterDateFns}
                            >
                                <MuiMaterials.DatePicker
                                    label="Date From"
                                    //className="graphDateFrom"
                                    value={dateFrom}
                                    sx={{
                                        width: "160px",
                                        margin: "10px 10px 10px 0px",
                                    }}
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                ".MuiDayCalendar-weekDayLabel": {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPaper-root": {
                                                    border: `1px solid ${getThemeColor('#cccccc', '#f4cc8e')}`,
                                                    borderRadius: "10px",
                                                    background: getThemeColor('#ffffff', '#2d2d2d'),
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiButtonBase-root": {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPickersDay-today": {
                                                    borderColor: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPickersDay-root.Mui-selected": {
                                                    background: getThemeColor('#cccccc', 'black'),
                                                    ':hover': {
                                                        bgcolor: getThemeColor('#cccccc', 'black'),
                                                    },
                                                    ':active': {
                                                        bgcolor: getThemeColor('#cccccc', 'black'),
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    onChange={setDateFrom}
                                />
                                <MuiMaterials.DatePicker
                                    label="Date To"
                                    //className="graphDateTo"
                                    value={dateTo}
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                ".MuiDayCalendar-weekDayLabel": {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPaper-root": {
                                                    border: `1px solid ${getThemeColor('#cccccc', '#f4cc8e')}`,
                                                    borderRadius: "10px",
                                                    background: getThemeColor('#ffffff', '#2d2d2d'),
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiButtonBase-root": {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPickersDay-today": {
                                                    borderColor: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                ".MuiPickersDay-root.Mui-selected": {
                                                    background: getThemeColor('#cccccc', 'black'),
                                                    ':hover': {
                                                        bgcolor: getThemeColor('#cccccc', 'black'),
                                                    },
                                                    ':active': {
                                                        bgcolor: getThemeColor('#cccccc', 'black'),
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                    sx={{
                                        width: "160px",
                                        margin: "10px 10px 10px 0px",
                                    }}
                                    onChange={setDateTo}
                                />
                            </MuiMaterials.LocalizationProvider>
                            <MuiMaterials.TextField
                                className="graphHourFrom"
                                label="Hour From"
                                sx={{
                                    //margin: "2px 0px",
                                    //marginLeft: "-5px",
                                    width: "95px",
                                    margin: "10px 10px 10px 0px",
                                }}
                                InputProps={{
                                    style: {
                                        fontSize: "18px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                    }
                                }}
                                inputProps={{
                                    style: {
                                        padding: "15px 5px",
                                        textAlign: "center",
                                    }
                                }}
                                defaultValue={hourFrom}
                                onFocus={() => {
                                    let graphHourFrom = window.document.querySelector('.graphHourFrom .MuiInputBase-input');
                                    if (graphHourFrom) {
                                        graphHourFrom.select();
                                    }
                                }}
                                onChange={(e) => {
                                    setHourFrom(+e.target.value);
                                }}
                                type="number"
                                min="1"
                                max="24"
                                inputmode="numeric"
                            />
                            <MuiMaterials.TextField
                                className="graphHourTo"
                                label="Hour To"
                                sx={{
                                    //margin: "2px 0px",
                                    //marginLeft: "10px",
                                    margin: "10px 0px",
                                    width: "95px",
                                }}
                                InputProps={{
                                    style: {
                                        fontSize: "18px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                    }
                                }}
                                inputProps={{
                                    style: {
                                        padding: "15px 5px",
                                        textAlign: "center",
                                    }
                                }}
                                defaultValue={hourTo}
                                onFocus={() => {
                                    let graphHourTo = window.document.querySelector('.graphHourTo .MuiInputBase-input');
                                    if (graphHourTo) {
                                        graphHourTo.select();
                                    }
                                }}
                                onChange={(e) => {
                                    setHourTo(+e.target.value);
                                }}
                                type="number"
                            />
                        </MuiMaterials.ThemeProvider>
                    </MuiMaterials.Box>
                    <MuiMaterials.Box
                        sx={{
                            display: "flex",
                            gap: "10px",
                            justifyContent: "center",
                            marginBottom: "18px",
                        }}
                    >
                        <MuiMaterials.Button
                            key={`co2-filter-cancel-${global.gw.theme}-${forceUpdate}`}
                            size="small"
                            variant="outlined"
                            sx={outlinedActionButtonSx()}
                            onClick={() => {
                                setIsFilterModalOpen(false);
                            }}
                        >
                            Cancel
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            key={`co2-filter-apply-${global.gw.theme}-${forceUpdate}`}
                            size="small"
                            variant="outlined"
                            sx={outlinedActionButtonSx()}
                            onClick={() => {
                                setIsFilterModalOpen(false);
                                setChartFilterOptions({
                                    binId: selectedBinId[0],
                                    binName: binIds.filter(b => b.binId === selectedBinId[0])[0]?.binName,
                                    deviceId: selectedDeviceId,
                                    dateFrom: dateFrom,
                                    dateTo: dateTo,
                                    hourFrom: hourFrom,
                                    hourTo: hourTo,
                                });
                            }}
                        >
                            Apply
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                    </MuiMaterials.Box>
            </MuiMaterials.Modal>
        );
    };

    return (
        <>
            <MuiMaterials.Button
                key={`co2-filter-button-${global.gw.theme}-${forceUpdate}`}
                sx={{
                    color: getThemeColor('#333333', '#f4cc8e'),
                    margin: "auto",
                    maxWidth: "40px",
                    maxHeight: "40px",
                    minWidth: "40px",
                    minHeight: "40px",
                    '&:hover': {
                        backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                    },
                }}
                onClick={e => {
                    setIsFilterModalOpen(true);
                }}
            >
                <TuneIcon />
            </MuiMaterials.Button>
            {renderFilterModal()}
        </>
    );
}
export default CarbonDioxideChartFilter;